// ═══════════════════════════════════════════════════════════════════════════
// 💾 CACHE MANAGER: LocalStorage Cache com TTL
// Gerencia cache de dados com expiração automática
// ═══════════════════════════════════════════════════════════════════════════

const CACHE_PREFIX = "sisrit_";
const CACHE_CONFIG = {
  rits: { ttl: 3600000 },           // 1 hora
  users: { ttl: 86400000 },          // 24 horas
  units: { ttl: 604800000 },         // 7 dias
  satAlerts: { ttl: 1800000 },       // 30 minutos
  analytics: { ttl: 900000 }         // 15 minutos
};

// Garbage collection: Remove expired entries and least recently used
const garbageCollect = (keepKey = null) => {
  const now = Date.now();
  let removed = 0;

  try {
    // Primeiro, remover entradas expiradas
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(CACHE_PREFIX) && key !== keepKey) {
        try {
          const cached = JSON.parse(localStorage.getItem(key));
          if (now - cached.timestamp > cached.ttl) {
            localStorage.removeItem(key);
            removed++;
          }
        } catch (e) {
          // Se não conseguir parsear, remover também
          localStorage.removeItem(key);
          removed++;
        }
      }
    });

    if (removed > 0) {
      console.log(`🧹 Garbage collected: ${removed} expired entries`);
    }

    // Se ainda estiver cheio, remover os caches menos recentemente usados (estratégia agressiva)
    const cacheEntries = Object.keys(localStorage)
      .filter(k => k.startsWith(CACHE_PREFIX) && k !== keepKey)
      .map(k => ({
        key: k,
        size: localStorage.getItem(k).length,
        timestamp: JSON.parse(localStorage.getItem(k)).timestamp
      }))
      .sort((a, b) => a.timestamp - b.timestamp);

    // Remover até 70% das entradas para liberar espaço
    const toRemove = Math.ceil(cacheEntries.length * 0.7);
    for (let i = 0; i < toRemove && cacheEntries[i]; i++) {
      localStorage.removeItem(cacheEntries[i].key);
      console.log(`🧹 Removed: ${cacheEntries[i].key.replace(CACHE_PREFIX, '')} (${Math.round(cacheEntries[i].size / 1024)}KB)`);
    }
  } catch (e) {
    console.warn('⚠️  Garbage collection error:', e);
  }
};

// Limpar TODOS os caches (último recurso)
const clearAllCachesForced = () => {
  try {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
    console.log(`🧹 All caches cleared (forced)`);
  } catch (e) {
    console.warn('⚠️  Failed to clear all caches:', e);
  }
};

// Armazenar dados em cache com timestamp
export const setCached = (key, data) => {
  try {
    const cacheData = {
      data,
      timestamp: Date.now(),
      ttl: CACHE_CONFIG[key]?.ttl || 3600000
    };
    localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(cacheData));
    console.log(`💾 Cached: ${key} (TTL: ${cacheData.ttl}ms)`);
    return true;
  } catch (e) {
    // Se QuotaExceededError, tentar garbage collection agressivo
    if (e.name === 'QuotaExceededError') {
      console.warn(`⚠️  Cache quota exceeded, running aggressive garbage collection...`);

      // Primeiro, tentar limpeza agressiva (remover 70% dos dados)
      garbageCollect(CACHE_PREFIX + key);

      try {
        const cacheData = {
          data,
          timestamp: Date.now(),
          ttl: CACHE_CONFIG[key]?.ttl || 3600000
        };
        localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(cacheData));
        console.log(`💾 Cached after aggressive GC: ${key}`);
        return true;
      } catch (retryErr) {
        // Se ainda não conseguir, limpar TUDO e tentar novamente (último recurso)
        console.warn(`⚠️  Still no space, clearing all caches...`);
        clearAllCachesForced();

        try {
          const cacheData = {
            data,
            timestamp: Date.now(),
            ttl: CACHE_CONFIG[key]?.ttl || 3600000
          };
          localStorage.setItem(CACHE_PREFIX + key, JSON.stringify(cacheData));
          console.log(`💾 Cached after clearing all: ${key}`);
          return true;
        } catch (finalErr) {
          console.error(`❌ Cache error (${key}) - data too large for cache:`, finalErr.message);
          // Dados são muito grandes, continuar sem cache
          return false;
        }
      }
    }

    console.error(`❌ Cache error (${key}):`, e);
    return false;
  }
};

// Recuperar dados do cache se ainda forem válidos
export const getCached = (key) => {
  try {
    const cached = localStorage.getItem(CACHE_PREFIX + key);
    if (!cached) {
      console.log(`🔄 Cache miss: ${key} (não existe)`);
      return null;
    }

    const { data, timestamp, ttl } = JSON.parse(cached);
    const age = Date.now() - timestamp;

    if (age > ttl) {
      console.log(`⏰ Cache expired: ${key} (${Math.floor(age / 1000)}s > ${Math.floor(ttl / 1000)}s)`);
      localStorage.removeItem(CACHE_PREFIX + key);
      return null;
    }

    console.log(`✅ Cache hit: ${key} (${Math.floor(age / 1000)}s old)`);
    return data;
  } catch (e) {
    console.error(`❌ Cache read error (${key}):`, e);
    return null;
  }
};

// Deletar cache específico
export const invalidateCache = (key) => {
  try {
    localStorage.removeItem(CACHE_PREFIX + key);
    console.log(`🗑️  Cache invalidated: ${key}`);
    return true;
  } catch (e) {
    console.error(`❌ Cache invalidate error (${key}):`, e);
    return false;
  }
};

// Deletar múltiplos caches
export const invalidateCaches = (keys) => {
  keys.forEach(key => invalidateCache(key));
};

// Limpar todo o cache
export const clearAllCache = () => {
  try {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(CACHE_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
    console.log("🗑️  All cache cleared");
    return true;
  } catch (e) {
    console.error("❌ Cache clear error:", e);
    return false;
  }
};

// Estimar tamanho do cache
export const getCacheSize = () => {
  let total = 0;
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith(CACHE_PREFIX)) {
      total += localStorage.getItem(key).length;
    }
  });
  return total;
};

// Obter informações de cache
export const getCacheInfo = () => {
  const info = {};
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith(CACHE_PREFIX)) {
      const cached = JSON.parse(localStorage.getItem(key));
      const realKey = key.replace(CACHE_PREFIX, "");
      const age = Date.now() - cached.timestamp;
      const ttl = cached.ttl;
      info[realKey] = {
        age: Math.floor(age / 1000) + "s",
        ttl: Math.floor(ttl / 1000) + "s",
        expired: age > ttl,
        size: localStorage.getItem(key).length
      };
    }
  });
  return info;
};

// Função helper para fetch com cache automático
export const fetchWithCache = async (key, fetcher, options = {}) => {
  // Tentar cache primeiro
  const cached = getCached(key);
  if (cached !== null) {
    return cached;
  }

  // Cache miss - fetch novo
  console.log(`🔄 Fetching: ${key}...`);
  try {
    const data = await fetcher();
    setCached(key, data);
    return data;
  } catch (e) {
    console.error(`❌ Fetch error (${key}):`, e);
    // Tentar retornar cache expirado como fallback
    const fallback = localStorage.getItem(CACHE_PREFIX + key);
    if (fallback) {
      console.log(`⚠️  Using expired cache as fallback: ${key}`);
      return JSON.parse(fallback).data;
    }
    throw e;
  }
};
